import React, { ComponentType, ReactElement } from 'react';

import dayjs from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useAtom } from 'jotai';
import { NextPage } from 'next';
import { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { appWithTranslation } from 'next-i18next';
import { useEffectOnce } from 'usehooks-ts';

import useAuth from '@/lib/hooks/useAuth';
import useGoogleAdsPageTransition from '@/lib/hooks/useGoogleAdsPageTransition';
// eslint-disable-next-line import/order
import { usePreserveScroll } from '@/lib/hooks/usePreserveScroll';
import '../styles/global.css';

import 'dayjs/locale/fr';
// import 'dayjs/locale/ar';

import {
  pageLoading,
  PageLoadingAction,
  LoadHeavyContentAtom,
  stickyBannerAtom,
} from '@/lib/store';
import AuthProvider from '@/modules/auth/providers/AuthProvider';
import NextBreadcrumb from '@/modules/core/components/NextBreadcrumb';
import { isIOSFacebook } from '@/modules/core/utils/utils';
import { NEXT_API_URL } from 'environment';

// Extending the NextPage type with getLayout
export type NextPageWithLayout<P = {}> = NextPage<P> & {
  getLayout?: (page: ReactElement, props: P) => ReactElement;
  layout?: ComponentType;
  authenticate?: boolean;
};

// Extending AppProps type with getLayout
type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

const GoogleAdsLoader = () => {
  const [loadHeavyContent] = useAtom(LoadHeavyContentAtom);

  if (typeof window !== 'undefined') {
    window.googletag = window.googletag || { cmd: [] };
  }
  useGoogleAdsPageTransition();

  return loadHeavyContent ? (
    <>
      <Script src="https://securepubads.g.doubleclick.net/tag/js/gpt.js" />
    </>
  ) : null;
};

if (typeof window !== 'undefined') {
  window.googletag = window.googletag || { cmd: [] };
}

// Main _app.tsx code
const MyApp = ({ Component, pageProps }: AppPropsWithLayout) => {
  const getLayout = Component.getLayout ?? ((page) => page);

  usePreserveScroll();
  const [, setIsLoading] = useAtom(pageLoading);
  const { logout, populateAtomWithUserData } = useAuth();
  const [, setStickyBanner] = useAtom(stickyBannerAtom);

  useEffectOnce(() => {
    fetch(`${NEXT_API_URL}/settings`)
      .then((res) => res.json())
      .then((settings) => {
        if (settings?.showStickyBanner) setStickyBanner(true);
      });
  });

  useEffectOnce(() => {
    populateAtomWithUserData();
    setTimeout(() => setIsLoading(PageLoadingAction.Stop), 2000);
    if (localStorage.getItem('newToken') !== 'true') {
      logout();
    }
  });

  const router = useRouter();

  // ---------- iOS Facebook redirect fix ----------
  useEffectOnce(() => {
    if (typeof window === 'undefined') return;

    // Check if we're in iOS Facebook browser
    const isInFacebookBrowser = isIOSFacebook();

    if (!isInFacebookBrowser) return;

    console.log('iOS Facebook browser detected, forcing external browser...');

    // Get current URL without protocol
    const currentUrl = window.location.href;
    const urlWithoutProtocol = currentUrl.replace(/^https?:\/\//, '');

    // Create a link element to force opening in external browser
    const link = document.createElement('a');
    link.href = currentUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';

    // Try multiple URL schemes to open in external browser
    // Method 1: Try Safari
    setTimeout(() => {
      window.location.href = `x-safari-https://${urlWithoutProtocol}`;
    }, 10);

    // Method 2: Try system default browser
    setTimeout(() => {
      window.location.href = `https://${urlWithoutProtocol}`;
      link.click();
    }, 100);
  });

  dayjs.locale(router.locale);
  dayjs.extend(relativeTime);
  dayjs.extend(LocalizedFormat);

  return getLayout(
    <>
      <NextBreadcrumb
        homeElement={
          <div className="text-gray-600 flex items-center gap-x-2">
            <svg
              className=" w-3 h-3"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="#718096"
              viewBox="0 0 20 20"
            >
              <path d="m19.707 9.293-2-2-7-7a1 1 0 0 0-1.414 0l-7 7-2 2a1 1 0 0 0 1.414 1.414L2 10.414V18a2 2 0 0 0 2 2h3a1 1 0 0 0 1-1v-4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v4a1 1 0 0 0 1 1h3a2 2 0 0 0 2-2v-7.586l.293.293a1 1 0 0 0 1.414-1.414Z" />
            </svg>
            Accueil
          </div>
        }
        separator={
          <div className="flex items-center">
            <svg
              className="w-2 h-2 m-1 text-gray-400"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 6 10"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m1 9 4-4-4-4"
              />
            </svg>
          </div>
        }
        activeClasses=""
        containerClasses=" hidden md:flex  items-center flex-wrap   py-1   "
        listClasses=" p-2 my-1 text-xs text-gray-600"
        capitalizeLinks
      />
      <AuthProvider>
        <Component {...pageProps} />
      </AuthProvider>
      <Script src="/rudderstack.js" strategy="lazyOnload" />
      <GoogleAdsLoader />
    </>,
    pageProps
  );
};

export default appWithTranslation(MyApp);
